// navigation-completion — Inspector → Validation tab.
//
// Composes the two authoritative navigation-VALIDATION surfaces for one
// document:
//   • LinksTab — the link-validation inventory (broken hyperlinks, re-validate).
//   • NavigationAssessment — the ICH/regional navigation-criteria assessment.
//
// This tab is NEVER disabled by being hidden: the restructure moved it out of a
// "Documents" sub-tab into a top-level tab precisely so link + navigation
// validation is always reachable. LinksTab therefore ALWAYS renders. The
// assessment needs the coherence preconditions (revision, artifact hash, policy
// version) the server last stamped, so it mounts its own authoritative envelope
// for the selected document; while that envelope is loading or failed, the
// assessment area shows an HONEST state (loading / could-not-load) — it never
// renders a reassuring "no criteria" blank that could be mistaken for a pass.

import { type ReactElement } from 'react';
import { LinksTab } from '../LinksTab';
import { NavigationAssessment } from './NavigationAssessment';
import { useNavigationEnvelope } from './useNavigationEnvelope';
import { navStyles } from './navShared';

export interface NavigationValidationPanelProps {
  readonly documentId: string;
  readonly submissionId: string;
  /** Documents:write — gates the "Re-validate now" affordance in LinksTab. */
  readonly canRevalidate: boolean;
  /**
   * phase-compiler-six-tabs — forwarded to LinksTab so a whole-submission
   * re-validate refreshes the parent's submission-level results.
   */
  readonly onRevalidated?: () => void;
}

export function NavigationValidationPanel({
  documentId,
  submissionId,
  canRevalidate,
  onRevalidated,
}: NavigationValidationPanelProps): ReactElement {
  const { env, loading, loadError } = useNavigationEnvelope(documentId);

  return (
    <div data-testid="nav-validation-panel">
      {/* Link-validation inventory — always available, never gated on the
          navigation envelope. */}
      <LinksTab
        documentId={documentId}
        submissionId={submissionId}
        canRevalidate={canRevalidate}
        {...(onRevalidated ? { onRevalidated } : {})}
      />

      {/* Regional navigation-criteria assessment, bound to the exact artifact the
          envelope reports. Honest states while that identity is unknown. */}
      <div style={{ marginTop: 16 }} data-testid="nav-validation-assessment">
        {loading && !env ? (
          <div style={navStyles.muted} data-testid="nav-validation-assessment-loading">
            Loading the navigation-criteria assessment…
          </div>
        ) : loadError && !env ? (
          <div style={navStyles.error} role="alert" data-testid="nav-validation-assessment-error">
            Could not load the navigation assessment identity: {loadError}
          </div>
        ) : env ? (
          <NavigationAssessment
            documentId={documentId}
            submissionId={submissionId}
            revision={env.revision}
            artifactSha256={env.artifactSha256}
            policyVersion={env.policyVersion}
          />
        ) : (
          <div style={navStyles.muted} data-testid="nav-validation-assessment-empty" />
        )}
      </div>
    </div>
  );
}

export default NavigationValidationPanel;
